import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import NewsLayout from "../../components/news/NewsLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, CheckCircle, Clock, XCircle, Eye, Loader2 } from "lucide-react";
import { useProject } from "../../context/ProjectContext";
import { themeFromHost, siteNameFromHost } from "../../lib/themeFromHost";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["business", "politics", "technology", "entertainment", "sports", "health"];

const WriterDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const { publicProject } = useProject();
  const theme = publicProject?.theme || themeFromHost();
  const siteName = publicProject?.name || siteNameFromHost() || undefined;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    summary: "",
    category: "business",
    image_url: "",
    tags: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/writer/articles`, { headers: getAuthHeaders() });
      setArticles(response.data);
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
        meta_keywords: formData.tags.split(",").map(t => t.trim()).filter(Boolean)
      };
      
      // Use user's project_id or a default
      const projectId = user.project_id || "default";
      
      await axios.post(`${API}/articles?project_id=${projectId}`, payload, { headers: getAuthHeaders() });
      toast.success("Article submitted for review!");
      setDialogOpen(false);
      setFormData({
        title: "",
        content: "",
        summary: "",
        category: "business",
        image_url: "",
        tags: ""
      });
      await fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit article");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "published":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "pending_review":
        return <Clock className="h-4 w-4 text-amber-600" />;
      case "declined":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      published: "bg-[#DCFCE7] text-[#166534]",
      pending_review: "bg-[#FEF3C7] text-[#92400E]",
      declined: "bg-[#FEE2E2] text-[#991B1B]",
      draft: "bg-[#F1F5F9] text-[#475569]"
    };
    return (
      <Badge className={styles[status] || styles.draft}>
        {status.replace("_", " ")}
      </Badge>
    );
  };

  return (
    <NewsLayout theme={theme} siteName={siteName}>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="writer-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold text-[#0F172A]">Writer Dashboard</h1>
            <p className="text-[#64748B]">Welcome back, {user?.name}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-article-button">
                <Plus className="h-4 w-4 mr-2" />
                New Article
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Write New Article</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Your article headline"
                    required
                    data-testid="article-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="article-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c} value={c} className="capitalize">
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="image_url">Image URL</Label>
                    <Input
                      id="image_url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://..."
                      data-testid="article-image-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="summary">Summary</Label>
                  <Textarea
                    id="summary"
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    placeholder="Brief summary of your article"
                    rows={2}
                    data-testid="article-summary-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Content *</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Write your article content here..."
                    rows={10}
                    required
                    data-testid="article-content-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="economy, finance, markets"
                    data-testid="article-tags-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="submit-article-button">
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Submit for Review
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{articles.length}</p>
                <p className="text-sm text-[#64748B]">Total</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">
                  {articles.filter(a => a.status === "published").length}
                </p>
                <p className="text-sm text-[#64748B]">Published</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">
                  {articles.filter(a => a.status === "pending_review").length}
                </p>
                <p className="text-sm text-[#64748B]">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">
                  {articles.filter(a => a.status === "declined").length}
                </p>
                <p className="text-sm text-[#64748B]">Declined</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Articles List */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Your Articles</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 bg-[#F8FAFC] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : articles.length > 0 ? (
              <div className="divide-y">
                {articles.map((article) => (
                  <div key={article.id} className="py-4 first:pt-0 last:pb-0" data-testid={`writer-article-${article.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        {article.image_url && (
                          <img
                            src={article.image_url}
                            alt=""
                            className="w-20 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#0F172A] truncate">{article.title}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-[#64748B]">
                            <span className="capitalize">{article.category}</span>
                            <span>•</span>
                            <span>{new Date(article.created_at).toLocaleDateString()}</span>
                          </div>
                          {article.review_comment && article.status === "declined" && (
                            <p className="text-sm text-red-600 mt-2">
                              Feedback: {article.review_comment}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(article.status)}
                        {article.status === "published" && (
                          <Link to={`/article/${article.slug}`}>
                            <Button variant="ghost" size="icon">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-[#64748B] opacity-50" />
                <h3 className="font-semibold text-[#0F172A] mb-2">No articles yet</h3>
                <p className="text-[#64748B] mb-4">Start writing your first article</p>
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Write Article
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </NewsLayout>
  );
};

export default WriterDashboard;
